/* global Excel */

import * as React from "react";
import { useState } from "react";
import { Stack, Text, DefaultButton, Dropdown, mergeStyles, getTheme } from "@fluentui/react";
import { insertMetricsSheet } from "../../sheetBuilder";
import { PEER_ANALYSIS_DEMOS, insertPeerAnalysisSheet } from "../../peerAnalysisTemplate";

const theme = getTheme();

const exampleCardStyle = mergeStyles({
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: "4px",
  padding: "8px 12px",
  backgroundColor: theme.palette.neutralLighterAlt,
});

const formulaStyle = mergeStyles({
  fontFamily: "Consolas, Menlo, monospace",
  fontSize: 11,
  backgroundColor: theme.palette.neutralLighter,
  padding: "4px 6px",
  borderRadius: 2,
  display: "block",
  overflowX: "auto",
  whiteSpace: "nowrap",
});

type Example = {
  title: string;
  description: string;
  formula: string;
  buttonText?: string;
  /** Optional picker beside the button; the selected index is passed to insert. */
  choices?: string[];
  insert?: (choice: number) => Promise<void>;
};

async function insertFormula(formula: string): Promise<void> {
  await Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.formulas = [[formula]];
    await context.sync();
  });
}

const DEMO_FORMULA = "=SHEETYBANKS.METRICS(A3:A5, C1:E1, C2:E2)";

// Builds a fresh worksheet showing the input-driven pattern (see sheetBuilder
// for the layout) — every header except "Cert" is a live input.
async function insertDemoSheet(): Promise<void> {
  await insertMetricsSheet({
    baseName: "SheetyBanks Demo",
    certs: [628, 3511, 4297],
    infoColumns: ["Name"],
    metricColumns: [
      { field: "ASSET", numberFormat: "#,##0" },
      { field: "ROA", quarter: "2025Q4", numberFormat: "0.00" },
      { field: "ROA", quarter: "2024Q4", numberFormat: "0.00" },
    ],
  });
}

const EXAMPLES: Example[] = [
  {
    title: "SHEETYBANKS.INFO — institution details",
    description: "Static facts about a bank by FDIC cert number: name, city, or state.",
    formula: '=SHEETYBANKS.INFO(628, "name")',
  },
  {
    title: "SHEETYBANKS.METRICS — one metric",
    description:
      'METRICS works in a single cell too: one cert, one field (ROA, ROE, ASSET, DEP, …). Omit the quarter for the latest value, or pass one like "2025Q4".',
    formula: '=SHEETYBANKS.METRICS(628, "ROA")',
  },
  {
    title: "SHEETYBANKS.METRICS — a whole table",
    description:
      'Point METRICS at header ranges — certs down the side, metrics and quarters across the top — and one formula spills the whole grid. The Name column uses INFO with its header cell as the field: type "city" in B1 and the column follows. This inserts a ready-made sheet you can edit.',
    formula: DEMO_FORMULA,
    buttonText: "Insert demo sheet",
    insert: insertDemoSheet,
  },
  {
    title: "SHEETYBANKS.BRANCHES — a bank's branch list",
    description:
      "Every branch office from the FDIC Summary of Deposits survey, one row per branch: name, city, state and deposits (thousands). Add SIMS_LATITUDE and SIMS_LONGITUDE for coordinates. Pass a year like 2023 for an earlier survey.",
    formula: '=SHEETYBANKS.BRANCHES(628, {"NAMEBR","CITYBR","STALPBR","DEPSUMBR"})',
  },
  {
    title: "SHEETYBANKS.MARKET — deposit share in a market",
    description:
      "Who holds the deposits in a county, MSA, state or zip: one row per institution, largest first, with branches, deposits, share and the prior-year comparison. The market is a state code, a county FIPS code, msa:code or zip:code — look codes up on the Markets tab.",
    formula:
      '=SHEETYBANKS.MARKET("48113", {"RANK","NAME","BRANCHES","DEPOSITS","SHARE","SHARE_PY"})',
  },
  {
    title: "Peer Analysis — a full dashboard worksheet",
    description:
      "A ready-made peer analysis: a latest-quarter scorecard for a bank and its peer group, " +
      "a five-year trend grid driven by a metric dropdown, and a chart of the bank vs. the " +
      "peer median. Save the populated workbook as .xlsx to share it as a template.",
    formula: "=SHEETYBANKS.METRICS(A8:A17, D6:L6, D7:L7)",
    buttonText: "Insert Peer Analysis",
    choices: PEER_ANALYSIS_DEMOS.map((spec) => spec.subjectName),
    insert: async (choice) => {
      await insertPeerAnalysisSheet(PEER_ANALYSIS_DEMOS[choice]);
    },
  },
];

const ExampleCard: React.FunctionComponent<{ example: Example }> = ({ example }) => {
  const [status, setStatus] = useState<"idle" | "done" | "error">("idle");
  const [choice, setChoice] = useState(0);

  const handleInsert = async () => {
    try {
      await (example.insert ? example.insert(choice) : insertFormula(example.formula));
      setStatus("done");
    } catch {
      setStatus("error");
    }
    setTimeout(() => setStatus("idle"), 2000);
  };

  return (
    <div className={exampleCardStyle}>
      <Stack tokens={{ childrenGap: 6 }}>
        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
          {example.title}
        </Text>
        <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
          {example.description}
        </Text>
        <code className={formulaStyle}>{example.formula}</code>
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 6 }}>
          {example.choices && (
            <Dropdown
              selectedKey={choice}
              options={example.choices.map((text, i) => ({ key: i, text }))}
              onChange={(_e, option) => option && setChoice(option.key as number)}
              styles={{
                root: { flexGrow: 1, minWidth: 0 },
                title: { height: 26, lineHeight: 24, fontSize: 12 },
                caretDownWrapper: { height: 26, lineHeight: 26 },
              }}
            />
          )}
          <DefaultButton
            text={
              status === "done"
                ? "Inserted ✓"
                : status === "error"
                  ? "Insert failed"
                  : (example.buttonText ?? "Insert example")
            }
            iconProps={{ iconName: "PageAdd" }}
            onClick={handleInsert}
            styles={{ root: { minWidth: 0, height: 26 }, label: { fontSize: 12 } }}
          />
        </Stack>
      </Stack>
    </div>
  );
};

const HelpGettingStarted: React.FunctionComponent = () => {
  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
        SheetyBanks adds custom functions that pull FDIC bank data straight into your workbook. Sign
        in first, then insert an example to see them in action — formulas go into the selected cell,
        and the demo sheet is created alongside your current one. Banks are identified by FDIC cert
        number — find one on the Company → Search tab.
      </Text>
      {EXAMPLES.map((example) => (
        <ExampleCard key={example.title} example={example} />
      ))}
      <Text variant="xSmall" styles={{ root: { color: theme.palette.neutralTertiary } }}>
        Tip: cells with no data return real #N/A errors, so IFNA and ISNA work as expected.
      </Text>
    </Stack>
  );
};

export default HelpGettingStarted;
